import React, {
  FunctionComponent, useEffect, useState, Fragment
} from 'react';
import cloneDeep from 'lodash.clonedeep';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RowNode, SelectionChangedEvent } from '@ag-grid-enterprise/all-modules';
import { RootState } from 'data/root.reducers';
import { GridSettings, WithAuditDetails, AuditDetails } from 'data/types';
import {
  AccessFunctionPrivileges,
  AF_USER_CATEGORY_SETTINGS,
  AccessFunctionDataActionDetails,
  AccessFunctionGridRowData,
  AccessFunction
} from 'data/accessFunction/accessFunction.types';
import * as actions from 'data/accessFunction/accessFunction.actions';
import * as dfniActions from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.actions';
import * as distributorConfigurationActions from 'data/distributorConfiguration/distributorConfiguration.actions';
import * as distributorConfigurationSelectors from 'data/distributorConfiguration/distributorConfiguration.selectors';
import * as userActions from 'users/users-actions';
import * as warrantActions from 'xcipio/warrants/warrant-actions';
import * as processOptionActions from 'data/processOption/processOption.actions';
import * as networkActions from 'data/network/network.actions';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as xdpActions from 'data/xdp/xdp.actions';
import * as nodeActions from 'data/nodes/nodes.actions';
import * as diameterActions from 'data/diameter/diameter.actions';
import * as selectors from 'data/accessFunction/accessFunction.selectors';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as templateSelectors from 'data/template/template.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import * as xdpSelectors from 'data/xdp/xdp.selectors';
import * as nodeSelectors from 'data/nodes/nodes.selectors';
import * as dfniSelectors from 'data/deliveryFunctionNetworkInterface/deliveryFunctionNetworkInterface.selectors';
import { getProcessOptionsList } from 'data/processOption/processOption.api';
import { AppToaster } from 'shared/toaster';
import ModalDialog from 'shared/modal/dialog';
import {
  getAuditDetails,
  getAuditFieldInfo,
  AuditService,
  AuditActionType
} from 'shared/userAudit/audit-constants';
import useConfirmActionDialog from 'utils/hooks/useConfirmActionDialog';
import { adaptAccessFunctionsGridRowData } from 'routes/AccessFunction/utils/accessFunctionList.adapters';
import { adaptTemplateOptions } from 'routes/AccessFunction/utils/accessFunctionTemplate.adapter';
import exportAfsToFile from 'routes/AccessFunction/utils/exportAfsToFile';
import { Buffer } from 'buffer';
import AccessFunctionGrid from './components/AccessFunctionGrid';
import {
  accessFunctionsColumnDefs,
  accessFunctionAutoGroupColumnDef,
  AccessFunctionMenuActions,
  accessFunctionsColumnDefsVersion
} from '../../AccessFunction.types';
import { isAccessFunctionTemplateAvailable } from '../../utils/isAccessFunctionTemplateAvailable';
import {
  CannotTriggerAuditDialogBody,
  DeleteDialogBody,
  MissingTemplateDialogBody,
  TriggerActionDialogBody
} from './components/DialogBody';
import isActionSupportedForAccessFunction from '../../utils/isActionSupportedForAccessFunction';
import {
  getDiameterAccessFunctions,
} from '../../utils/getDiameterAccessFunctions';
import {
  ExportOption
} from './components/DialogBody/ExportReportDialogBody';

type RouterParams = {
  history: string;
};

const mapState = (state: RootState) => ({
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  deliveryFunctionId: state.global.selectedDF,
  data: selectors.getPoiAccessFunctions(state),
  enabledAccessFunctions: supportedFeaturesSelectors.getEnabledAccessFunction(state),
  selectedAccessFunction: selectors.getSelectedAccessFunction(state),
  isDeletingAccessFunction: selectors.isDeletingAccessFunction(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  afsExportLimit: discoverySelectors.getAfsExportLimit(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  accessFunctionTemplates: templateSelectors.getEnabledAccessFunctionFormTemplatesByKey(state),
  networks: networkSelectors.getNetworks(state),
  xdps: xdpSelectors.getXdps(state),
  xcipioNodes: nodeSelectors.getNodes(state),
  distributorConfigurationsByAccessFunctionType: distributorConfigurationSelectors.getDistributorConfigurationsByAccessFunctionType(state),
  deliveryFunctionNetworkInterfaces: dfniSelectors.getDeliveryFunctionNetworkInterfaceList(state)
});

const mapDispatch = {
  getAccessFunctions: actions.getAccessFunctionsWithViewOptions,
  goToAccessFunctionCreate: actions.goToAccessFunctionCreate,
  goToAccessFunctionEdit: actions.goToAccessFunctionEdit,
  goToAccessFunctionView: actions.goToAccessFunctionView,
  goToAccessFunctionCopy: actions.goToAccessFunctionCopy,
  getGridSettings: userActions.getUserSettings,
  saveGridSettings: userActions.putUserSettings,
  selectAccessFunction: actions.selectAccessFunction,
  deleteAccessFunction: actions.deleteAccessFunction,
  getSupportedFeature: warrantActions.getWarrantFormConfig,
  auditAccessFunction: actions.auditAccessFunction,
  resetAccessFunction: actions.resetAccessFunction,
  refreshAccessFunction: actions.refreshAccessFunction,
  getTemplates: warrantActions.getAllTemplates,
  getConfiguredAccessFunctions: actions.getConfiguredAccessFunctions,
  getProcessOptions: processOptionActions.getProcessOptions,
  getNetworks: networkActions.getNetworks,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getXdps: xdpActions.getXdps,
  getXcipioNodes: nodeActions.getNodes,
  getDistributorConfigurationsWithView: distributorConfigurationActions.getDistributorConfigurationsWithView,
  getDfnis: dfniActions.getDeliveryFunctionNetworkInterfaceList,
  getDiameters: diameterActions.getDiameters
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>;

export type Props = PropsFromRedux & PropsFromRouter;

export const AccessFunctionList: FunctionComponent<Props> = ({
  beforeLogout,
  privileges,
  deliveryFunctionId,
  data = [],
  history,
  selectedAccessFunction,
  enabledAccessFunctions,
  isAuditEnabled,
  userId,
  userName,
  accessFunctionTemplates,
  networks,
  xdps,
  xcipioNodes,
  distributorConfigurationsByAccessFunctionType,
  deliveryFunctionNetworkInterfaces,
  afsExportLimit,
  getDiameters,
  goToAccessFunctionCreate,
  goToAccessFunctionEdit,
  goToAccessFunctionView,
  goToAccessFunctionCopy,
  getAccessFunctions,
  getGridSettings,
  saveGridSettings,
  selectAccessFunction,
  deleteAccessFunction,
  getSupportedFeature,
  auditAccessFunction,
  resetAccessFunction,
  refreshAccessFunction,
  getTemplates,
  getConfiguredAccessFunctions,
  getProcessOptions,
  getNetworks,
  getSecurityInfos,
  getXdps,
  getXcipioNodes,
  getDistributorConfigurationsWithView,
  getDfnis
}: Props) => {
  const [accessFunctionGridRowData, setAccessFunctionGridRowData] = useState<AccessFunctionGridRowData[]>([]);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [confirmActionDialog, setConfirmActionDialog] = useConfirmActionDialog();
  const { t } = useTranslation();
  const [diameterAfs, setDiameterAfs] = useState<{ [key: string]: AccessFunction; }>({});

  useEffect(() => {
    AppToaster.clear();
    getSupportedFeature(deliveryFunctionId);
    getAccessFunctions(deliveryFunctionId);
    getDistributorConfigurationsWithView(deliveryFunctionId);
    getTemplates();
    getConfiguredAccessFunctions(deliveryFunctionId);
    getProcessOptionsList(deliveryFunctionId);
    getNetworks(deliveryFunctionId);
    getSecurityInfos(deliveryFunctionId);
    getXdps(deliveryFunctionId);
    getXdps(deliveryFunctionId);
    getXcipioNodes(deliveryFunctionId);
    getDfnis(deliveryFunctionId);
    getDiameters(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [
    deliveryFunctionId,
    getAccessFunctions,
    getDistributorConfigurationsWithView,
    getSupportedFeature,
    getTemplates,
    getConfiguredAccessFunctions,
    getProcessOptions,
    getNetworks,
    getSecurityInfos,
    getXdps,
    getXcipioNodes,
    getDfnis,
    getDiameters
  ]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
    const availableTemplates = adaptTemplateOptions(
      accessFunctionTemplates,
      [],
      [],
      [],
      networks,
      [],
      [],
      [],
      xdps,
      [],
      enabledAccessFunctions,
      xcipioNodes,
      [],
      []
    );

    const gridRowData = adaptAccessFunctionsGridRowData(
      data,
      enabledAccessFunctions,
      availableTemplates,
      networks,
      xdps,
      distributorConfigurationsByAccessFunctionType
    );

    setAccessFunctionGridRowData(gridRowData);
  }, [
    data,
    enabledAccessFunctions,
    accessFunctionTemplates,
    networks,
    xdps,
    xcipioNodes,
    distributorConfigurationsByAccessFunctionType
  ]);

  useEffect(() => {
    const afs = getDiameterAccessFunctions(data);
    setDiameterAfs(afs);
  }, [data]);

  const handleRefresh = () => getAccessFunctions(deliveryFunctionId);

  const handleCreateNew = () => goToAccessFunctionCreate(history);

  const handleSelection = (event: SelectionChangedEvent) => {
    return selectAccessFunction(adaptSelection(event.api.getSelectedRows()));
  };

  // DX-7042: This function removes the "_interfaceType" and passes actual ID
  const adaptSelection = (accessFunctions: AccessFunction[]) => {
    const accessFunctionCopy: AccessFunction[] = cloneDeep(accessFunctions);
    accessFunctionCopy.forEach((af) => {
      af.id = af.id?.split('_')[0];
    });
    return accessFunctionCopy;
  };

  const handleSaveGridSettings = (gridSettings: GridSettings) =>
    saveGridSettings(AF_USER_CATEGORY_SETTINGS, userName, gridSettings);

  const handleGetGridSettings = () =>
    getGridSettings(AF_USER_CATEGORY_SETTINGS, userName);

  const handleShowActionMenuItem = (
    type: AccessFunctionMenuActions,
    accessFunctionTag: string
  ) => {
    if (
      AccessFunctionMenuActions.Copy === type &&
      accessFunctionTag === 'DRVIRTUAL'
    ) {
      return false;
    }
    if (
      AccessFunctionMenuActions.Audit === type &&
      (accessFunctionTag === 'NOK_5GAMF_LOCSVC' ||
        accessFunctionTag === 'NOKIA_CMG_U')
    ) {
      return false;
    }
    if (
      AccessFunctionMenuActions.Copy === type &&
      accessFunctionTag === 'PVOIP'
    ) {
      return false;
    }
    if (
      type === AccessFunctionMenuActions.Reset ||
      type === AccessFunctionMenuActions.Refresh
    ) {
      return isActionSupportedForAccessFunction(
        type,
        accessFunctionTag,
        enabledAccessFunctions
      );
    }

    return true;
  };

  const showPopupForUnavailableTemplate = (
    title: string,
    accessFunctionTag: string
  ) => {
    const accessFunctionTitle = enabledAccessFunctions[accessFunctionTag].title;

    setConfirmActionDialog(
      t('accessFunction.grid.missingTemplate.title', { title }),
      <MissingTemplateDialogBody name={accessFunctionTitle} />,
      t('common.button.close')
    );
  };

  const handleSelectItemMenuAction = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    const accessFunction = selectedAccessFunction[0];
    if (!accessFunction) {
      return;
    }

    const { name, tag, provisioningInterfaces } = accessFunction;

    if (event?.type === AccessFunctionMenuActions.Delete) {
      setConfirmActionDialog(
        t('accessFunction.deletePopup.title'),
        <DeleteDialogBody name={name} />,
        t('accessFunction.deletePopup.action'),
        handleDeleteAccessFunction
      );
    } else if (event?.type === AccessFunctionMenuActions.Audit) {
      if (provisioningInterfaces && provisioningInterfaces.length > 0) {
        setConfirmActionDialog(
          t('accessFunction.auditPopup.title'),
          <TriggerActionDialogBody>
            {t('accessFunction.auditPopup.message', { name })}
          </TriggerActionDialogBody>,
          t('accessFunction.auditPopup.action'),
          handleAuditAccessFunction
        );
      } else {
        setConfirmActionDialog(
          t('accessFunction.auditPopup.title'),
          <CannotTriggerAuditDialogBody name={name} />,
          t('common.button.close')
        );
      }
    } else if (event?.type === AccessFunctionMenuActions.Reset) {
      setConfirmActionDialog(
        t('accessFunction.resetPopup.title'),
        <TriggerActionDialogBody>
          {t('accessFunction.resetPopup.message', { name })}
        </TriggerActionDialogBody>,
        t('accessFunction.resetPopup.action'),
        handleResetAccessFunction
      );
    } else if (event?.type === AccessFunctionMenuActions.Refresh) {
      setConfirmActionDialog(
        t('accessFunction.refreshPopup.title'),
        <TriggerActionDialogBody>
          {t('accessFunction.refreshPopup.message', { name })}
        </TriggerActionDialogBody>,
        t('accessFunction.refreshPopup.action'),
        handleRefreshAccessFunction
      );
    } else if (event?.type === AccessFunctionMenuActions.Edit) {
      if (
        isAccessFunctionTemplateAvailable(
          accessFunctionTemplates,
          selectedAccessFunction[0]
        )
      ) {
        goToAccessFunctionEdit(history);
      } else {
        showPopupForUnavailableTemplate(t('accessFunction.grid.edit'), tag);
      }
    } else if (event?.type === AccessFunctionMenuActions.View) {
      if (
        isAccessFunctionTemplateAvailable(
          accessFunctionTemplates,
          selectedAccessFunction[0]
        )
      ) {
        goToAccessFunctionView(history);
      } else {
        showPopupForUnavailableTemplate(t('accessFunction.grid.view'), tag);
      }
    } else if (event?.type === AccessFunctionMenuActions.Copy) {
      if (
        isAccessFunctionTemplateAvailable(
          accessFunctionTemplates,
          selectedAccessFunction[0]
        )
      ) {
        goToAccessFunctionCopy(history);
      } else {
        showPopupForUnavailableTemplate(t('accessFunction.grid.copy'), tag);
      }
    }
  };

  const handleDeleteAccessFunction = () => {
    const accessFunction = selectedAccessFunction[0];
    if (accessFunction == null || accessFunction.id == null) {
      return;
    }

    const accessFunctionBody: WithAuditDetails<void> = { data: undefined };

    deleteAccessFunction(
      deliveryFunctionId,
      accessFunction.id,
      accessFunction.name,
      accessFunctionBody
    );
  };

  const handleExecuteActionAccessFunction = (
    action: 'AUDIT' | 'RESET' | 'DWNLD',
    executeActionAccessFunction: CallableFunction
  ) => {
    const accessFunction = selectedAccessFunction[0];
    if (accessFunction == null || accessFunction.id == null) {
      return;
    }

    const accessFunctionBody: WithAuditDetails<AccessFunctionDataActionDetails> = {
      data: { action, name: accessFunction.name }
    };

    executeActionAccessFunction(
      deliveryFunctionId,
      accessFunction.id,
      accessFunctionBody
    );
  };

  const handleAuditAccessFunction = () => {
    handleExecuteActionAccessFunction(
      'AUDIT',
      auditAccessFunction
    );
  };

  const handleResetAccessFunction = () => {
    handleExecuteActionAccessFunction(
      'RESET',
      resetAccessFunction
    );
  };

  const handleRefreshAccessFunction = () => {
    handleExecuteActionAccessFunction(
      'DWNLD',
      refreshAccessFunction
    );
  };

  const exportAfs = (afIds: string[], headers: string[], gridRowData: RowNode[], option: ExportOption) => {
    const filtered: AccessFunction[] = [];
    afIds.map((afid) => {
      const found = data.find((x) => afid === Buffer.from(x.id as string, 'base64').toString('binary'));
      if (found) {
        filtered.push(found);
      }
    });
    exportAfsToFile(
      filtered,
      headers,
      gridRowData,
      enabledAccessFunctions,
      accessFunctionTemplates,
      networks,
      xdps,
      distributorConfigurationsByAccessFunctionType,
      option,
      afsExportLimit
    );
  };

  const hasPrivilegesToCreate = privileges.includes(
    AccessFunctionPrivileges.Edit
  );
  const hasPrivilegeToDelete = privileges.includes(
    AccessFunctionPrivileges.Delete
  );
  const hasPrivilegeToView = privileges.includes(AccessFunctionPrivileges.View);

  return (
    <Fragment>
      <AccessFunctionGrid
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        hasPrivilegeToView={hasPrivilegeToView}
        data={accessFunctionGridRowData}
        noResults={false}
        beforeLogout={beforeLogout}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelection}
        onSaveGridSettings={handleSaveGridSettings}
        onGetGridSettings={handleGetGridSettings}
        onSelectItemMenuAction={handleSelectItemMenuAction}
        onShowActionMenuItem={handleShowActionMenuItem}
        lastUpdatedAt={lastUpdatedAt}
        columnDefs={accessFunctionsColumnDefs}
        accessFunctionsColumnDefsVersion={accessFunctionsColumnDefsVersion}
        autoGroupColumnDef={accessFunctionAutoGroupColumnDef}
        suppressAggFuncInHeader
        groupSelectsChildren
        suppressRowClickSelection
        enableGroupSelection
        handleExport={exportAfs}
      />
      <ModalDialog
        width="500px"
        isOpen={confirmActionDialog.isOpen}
        title={confirmActionDialog.title}
        customComponent={confirmActionDialog.customComponent}
        actionText={confirmActionDialog.actionText}
        onSubmit={confirmActionDialog.onSubmit}
        onClose={confirmActionDialog.onClose}
        doNotDisplayCancelButton={confirmActionDialog.hideCancel}
      />
    </Fragment>
  );
};

export default withRouter(connector(AccessFunctionList));
